/* ------------------------------------------------------------------ */
/*  Shared types for the Quiz & Global Test generation feature         */
/* ------------------------------------------------------------------ */

/** Result of the lightweight analysis call (step 2). */
export type LessonAnalysis = {
  lessonTopic: string;
  gradeLevel: string;
  hasReadingPassage: boolean;
  grammarTopic: string | null;
  vocabularyTheme: string | null;
  functionsDetected: string | null;
  writingType: string | null;
  componentNotes: string;
};

/* ---- Test configuration (teacher choices, step 3) ---- */

export type TestType = "global_test" | "quiz";
export type LanguageLevel = "A1" | "A2" | "B1" | "B2";
export type ComplexityLevel = "easy" | "medium" | "hard";

export type ComprehensionExerciseType =
  | "mcq"
  | "true_false"
  | "answer_questions"
  | "referents"
  | "complete_sentences";

export type GrammarExerciseType =
  | "verb_form"
  | "adverb_position"
  | "mcq"
  | "write_questions"
  | "complete_sentences";

export type VocabularyExerciseType =
  | "fill_gap"
  | "odd_word"
  | "match_definitions"
  | "complete_sentences";

export type FunctionsExerciseType =
  | "situation_response"
  | "write_question"
  | "complete_dialogue";

export type SectionConfig = {
  included: boolean;
  points: number;
};

export type ComprehensionConfig = SectionConfig & {
  exercises: ComprehensionExerciseType[];
};

export type LanguageConfig = SectionConfig & {
  grammar: { included: boolean; points: number; exercises: GrammarExerciseType[] };
  vocabulary: { included: boolean; points: number; exercises: VocabularyExerciseType[] };
  functions: { included: boolean; points: number; exercises: FunctionsExerciseType[] };
};

export type WritingConfig = SectionConfig & {
  guided: boolean;
};

export type TestConfig = {
  testType: TestType;
  languageLevel: LanguageLevel;
  complexity: ComplexityLevel;
  comprehension: ComprehensionConfig;
  language: LanguageConfig;
  writing: WritingConfig;
};

/* ---- Generated test output (step 4/5) ---- */

export type TestExerciseItem = {
  number: number;
  question: string;
  options: string[] | null;
  answer: string;
};

export type LegacyTestExercise = {
  label: string;
  instruction: string;
  points: number;
  pointsNote: string;
  items: TestExerciseItem[];
};

export type WordBoxBlock = {
  type: "word_box";
  words: string[];
};

export type NumberedListBlock = {
  type: "numbered_list";
  items: { number: number; text: string }[];
};

export type BulletListBlock = {
  type: "bullet_list";
  items: string[];
};

export type TableBlock = {
  type: "table";
  columns: string[];
  rows: string[][];
};

export type DialogueBlock = {
  type: "dialogue";
  lines: { speaker: string; text: string }[];
};

export type TextBlock = {
  type: "text";
  content: string;
};

export type ReadingPassageBlock = {
  type: "reading_passage";
  content: string;
};

export type PrintLinesBlock = {
  type: "print_lines";
  count: number;
};

export type TaskBlock =
  | WordBoxBlock
  | NumberedListBlock
  | BulletListBlock
  | TableBlock
  | DialogueBlock
  | TextBlock
  | ReadingPassageBlock
  | PrintLinesBlock;

export type TaskAnswer = {
  blockIndex: number;
  itemRef: number | string;
  answer: string;
};

export type TestTask = {
  title: string;
  points: number;
  pointsLabel: string;
  blocks: TaskBlock[];
  answers?: TaskAnswer[];
};

export type TestExercise = LegacyTestExercise | TestTask;

export type TestSection = {
  number: "I" | "II" | "III";
  title: "COMPREHENSION" | "LANGUAGE" | "WRITING";
  totalPoints: number;
  readingText: string | null;
  exercises: TestExercise[];
};

export type WritingTask = {
  instruction: string;
  scaffolding: string | null;
  points: number;
  markingCriteria: string;
};

export type TestOutput = {
  testType: TestType;
  grade: string;
  lessonTopic: string;
  sections: TestSection[];
  writingTask: WritingTask;
};

/* ---- Default point distributions ---- */

export const GLOBAL_TEST_DEFAULTS: TestConfig = {
  testType: "global_test",
  languageLevel: "A2",
  complexity: "medium",
  comprehension: {
    included: true,
    points: 7,
    exercises: ["mcq", "true_false", "answer_questions"],
  },
  language: {
    included: true,
    points: 8,
    grammar: { included: true, points: 3, exercises: ["verb_form"] },
    vocabulary: { included: true, points: 3, exercises: ["fill_gap"] },
    functions: { included: true, points: 2, exercises: ["situation_response"] },
  },
  writing: {
    included: true,
    points: 5,
    guided: true,
  },
};

export const QUIZ_DEFAULTS: TestConfig = {
  testType: "quiz",
  languageLevel: "A2",
  complexity: "medium",
  comprehension: {
    included: true,
    points: 3,
    exercises: ["mcq", "true_false", "answer_questions"],
  },
  language: {
    included: true,
    points: 4,
    grammar: { included: true, points: 1.5, exercises: ["verb_form"] },
    vocabulary: { included: true, points: 1.5, exercises: ["fill_gap"] },
    functions: { included: true, points: 1, exercises: ["situation_response"] },
  },
  writing: {
    included: true,
    points: 3,
    guided: true,
  },
};

/** Target total for each test type. */
export function testTargetTotal(testType: TestType): number {
  return testType === "quiz" ? 10 : 20;
}

/** Compute the current total from a config. */
export function computeConfigTotal(config: TestConfig): number {
  let total = 0;
  if (config.comprehension.included) total += config.comprehension.points;
  if (config.language.included) total += config.language.points;
  if (config.writing.included) total += config.writing.points;
  return Math.round(total * 100) / 100; // avoid float drift
}
